import React, { useCallback, useMemo, useState } from 'react';

import classNames from 'clsx';

import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material';

import Calendar from '~/components/Common/Calendar';

import { addDays, dateNowUtc, formatDateDay, formatDateISO } from '~/services/TimeService';

import { InventoryLimit } from '../../../hooks/apis/reservation/useOfferInventoryLimits';
import getCalendarGrid from '../../Vendors/Properties/helpers/getCalendarGrid';

interface Props {
  inventoryLimits: InventoryLimit[];
  selectedDates: Set<string>;
  selectMode: 'free' | 'range';
  onSelectModeChange: (mode: 'free' | 'range') => void;
  onDatesChange: (dates: Set<string>) => void;
  onSetInventory: () => void;
  onClearInventory: (dates: string[]) => void;
}

function datesBetween(start: string, end: string): string[] {
  const result: string[] = [];
  let cur = dateNowUtc(start);
  while (formatDateISO(cur) <= end) {
    result.push(formatDateISO(cur));
    cur = addDays(1, cur);
  }
  return result;
}

export default function OfferInventoryCalendar({
  inventoryLimits,
  selectedDates,
  selectMode,
  onSelectModeChange,
  onDatesChange,
  onSetInventory,
  onClearInventory,
}: Props) {
  const theme = useTheme();
  const [offset, setOffset] = useState(0);
  const [rangeStart, setRangeStart] = useState<string | null>(null);

  const inventoryByDate = useMemo(() => {
    const map = new Map<string, InventoryLimit>();
    for (const limit of inventoryLimits) {
      map.set(limit.date, limit);
    }
    return map;
  }, [inventoryLimits]);

  const firstDay = useMemo(() => dateNowUtc(), []);

  const affectedInventoryLimits = useMemo(
    () => inventoryLimits.filter((l) => selectedDates.has(l.date)),
    [inventoryLimits, selectedDates],
  );

  const grid = useMemo(() => {
    const raw = getCalendarGrid(firstDay, 2, offset);
    return raw.map((month) => ({
      date: month.date,
      weeks: month.weeks.map((week: Array<Date | null>) => week.map((day) => ({ day }))),
    }));
  }, [firstDay, offset]);

  const handleDayClick = useCallback(
    (dateStr: string) => {
      if (selectMode === 'free') {
        const next = new Set(selectedDates);
        if (next.has(dateStr)) {
          next.delete(dateStr);
        } else {
          next.add(dateStr);
        }
        onDatesChange(next);
      } else {
        if (!rangeStart) {
          setRangeStart(dateStr);
          onDatesChange(new Set([dateStr]));
        } else {
          const [start, end] = rangeStart <= dateStr ? [rangeStart, dateStr] : [dateStr, rangeStart];
          onDatesChange(new Set(datesBetween(start, end)));
          setRangeStart(null);
        }
      }
    },
    [selectMode, selectedDates, rangeStart, onDatesChange],
  );

  const calendarDayComponent = useCallback(
    ({ dayDate, idx }: { dayDate: { day: Date | null }; idx: number }) => {
      if (!dayDate.day) {
        return <div className="availability-day" key={idx} />;
      }

      const dateStr = formatDateISO(dayDate.day);
      const limit = inventoryByDate.get(dateStr);
      const isSelected = selectedDates.has(dateStr);
      const soldOut = limit && limit.count >= limit.total;

      const isUpdate = isSelected && !!limit;
      const classes = classNames('availability-day', { selected: isSelected && !limit });

      return (
        <div
          className={classes}
          key={idx}
          data-testid={`inventory-day-${dateStr}`}
          onClick={() => handleDayClick(dateStr)}
          style={
            isUpdate
              ? { backgroundColor: theme.palette.warning.light, border: `1px solid ${theme.palette.warning.main}` }
              : undefined
          }
        >
          <div className="date">{formatDateDay(dayDate.day)}</div>
          {limit ? (
            <div className="counts" style={{ color: soldOut ? '#d32f2f' : undefined }}>
              {limit.count}/{limit.total}
            </div>
          ) : (
            <div className="counts" style={{ color: '#ccc' }}>
              —
            </div>
          )}
        </div>
      );
    },
    [inventoryByDate, selectedDates, handleDayClick],
  );

  const hasSelection = selectedDates.size > 0;
  const selectedNewCount = Array.from(selectedDates).filter((d) => !inventoryByDate.has(d)).length;
  const selectedExistingCount = affectedInventoryLimits.length;

  const setInventoryLabel =
    selectedExistingCount === 0
      ? 'Set inventory'
      : selectedNewCount === 0
        ? 'Update inventory'
        : 'Set / update inventory';

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          value={selectMode}
          exclusive
          onChange={(_, v) => {
            if (!v) return;
            onSelectModeChange(v);
            onDatesChange(new Set());
            setRangeStart(null);
          }}
          size="small"
        >
          <ToggleButton value="free">Free select</ToggleButton>
          <ToggleButton value="range">Range</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Set a maximum room count for the selected dates">
            <span>
              <Button variant="contained" size="small" disabled={!hasSelection} onClick={onSetInventory}>
                {setInventoryLabel}
              </Button>
            </span>
          </Tooltip>
          {affectedInventoryLimits.length > 0 && (
            <Tooltip title={`Delete inventory limits on ${affectedInventoryLimits.length} selected date(s)`}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                  const dates = Array.from(selectedDates).filter((d) =>
                    affectedInventoryLimits.some((l) => l.date === d),
                  );
                  onClearInventory(dates);
                }}
              >
                Clear inventory ({affectedInventoryLimits.length})
              </Button>
            </Tooltip>
          )}
          {hasSelection && (
            <Tooltip title="Deselect all highlighted dates">
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  onDatesChange(new Set());
                  setRangeStart(null);
                }}
              >
                Deselect ({selectedDates.size})
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {selectMode === 'range' && rangeStart && (
        <Typography variant="caption" color="text.secondary">
          Range start: {rangeStart} — click a second date to complete the selection
        </Typography>
      )}

      <div className="availability-calendar" data-no-dark-override>
        <div className="buttons">
          <button onClick={() => setOffset((o) => o - 1)}>&lt;</button>
          <button onClick={() => setOffset((o) => o + 1)}>&gt;</button>
        </div>
        <Calendar grid={grid} calendarDayComponent={calendarDayComponent} />
      </div>

      <Stack direction="row" spacing={3} flexWrap="wrap">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 14, height: 14, bgcolor: '#b6e7ff', border: '1px solid #7ca5b7', borderRadius: '2px' }} />
          <Typography variant="caption">Selected (new)</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box
            sx={{
              width: 14,
              height: 14,
              bgcolor: 'warning.light',
              border: '1px solid',
              borderColor: 'warning.main',
              borderRadius: '2px',
            }}
          />
          <Typography variant="caption">Selected (update)</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          <strong style={{ color: '#d32f2f' }}>N/N</strong> = Sold out
        </Typography>
      </Stack>
    </Stack>
  );
}
